import React, { useEffect, useState, useRef } from 'react';
import { captureFirstFrame } from '../../../lib/video/captureFirstFrame';

interface VideoPlayerProps {
  src: string;
  poster?: string;
  controls?: boolean;
  autoPlay?: boolean;
  onEnded?: () => void;
  loopCount?: number;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({ src, poster, controls = true, autoPlay = false, onEnded, loopCount }) => {
  const [posterSrc, setPosterSrc] = useState<string | undefined>(poster);
  const loopCounterRef = useRef(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    let mounted = true;
    if (!poster) {
      if (src) {
        if (typeof window !== 'undefined') {
          (async () => {
            const data = await captureFirstFrame(src);
            if (mounted) {
              if (data) setPosterSrc(data);
            }
          })();
        }
      }
    }
    return () => { mounted = false; };
  }, [src, poster]);

  // Reset the loop counter when source or loopCount changes
  useEffect(() => {
    loopCounterRef.current = 0;
  }, [src, loopCount]);

  const handleEnded = () => {
    if (typeof loopCount === 'number' && loopCount > 0) {
      loopCounterRef.current += 1;
      if (loopCounterRef.current < loopCount) {
        const v = videoRef.current;
        if (v) {
          v.currentTime = 0;
          v.play().catch(() => {});
          return;
        }
      }
    }
    if (onEnded) onEnded();
  };

  return (
    <video 
      ref={videoRef}
      className="video-player" 
      poster={posterSrc} 
      controls={controls} 
      autoPlay={autoPlay} 
      onEnded={handleEnded}
      style={{ 
        width: '100%', 
        height: 'auto', 
        maxWidth: '100%',
        display: 'block'
      }}
    >
      <source src={src} type="video/mp4" />
      <source src={src.replace('.mp4', '.webm')} type="video/webm" />
      {posterSrc ? <img src={posterSrc} alt="Video thumbnail" /> : null}
    </video>
  );
};

export default VideoPlayer;
